import { accessControlListApiRef } from '@agilelab/plugin-wb-access-control';
import { ACLSystemOwned } from '@agilelab/plugin-wb-access-control-common';
import {
  ACL,
  customAlertApiRef,
  useEnvironmentsContext,
  useUserProfile,
  WbCard,
  WbDockableGroup,
  WbDockablePanel,
  WbDockableResizeHandle,
  WbEnvironmentSelector,
} from '@agilelab/plugin-wb-platform';
import { useApi } from '@mainstage/core-plugin-api';
import { makeStyles } from '@material-ui/core';
import { useEffect, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { AccessControlList } from './AccessControlListByDp';
import { SystemList } from './SystemList';

export interface SystemItem {
  id: number;
  domain: string;
  systemVersion: string;
  displayName: string;
  kind: string;
}

export interface ACLByDP extends ACL {
  id: number;
  refs?: { user?: boolean; groups?: string[] };
  /**
   * Consumable type (e.g., table, file, event)
   */
  type: string;
  parent?: { id: number; displayName: string };
}

const useStyles = makeStyles(
  theme => ({
    dockableContainer: {
      flex: '1 1 0',
      [theme.breakpoints.down('sm')]: {
        flex: '1 1 auto',
      },
    },
  }),
  { name: 'DataAccessTab' },
);

export const DataAccessTab = () => {
  const classes = useStyles();
  const accessControlListClient = useApi(accessControlListApiRef);
  const alertApi = useApi(customAlertApiRef);
  const { environment } = useEnvironmentsContext();
  const [selectedRow, setSelectedRow] = useState<SystemItem | undefined>(
    undefined,
  );
  const [systemSelected, setSystemSelected] = useState<ACLSystemOwned>();
  const [systems, setSystems] = useState<SystemItem[]>([]);
  const { backstageIdentity } = useUserProfile();

  const { value } = useAsync(async () => {
    if (backstageIdentity) {
      try {
        return (
          (await accessControlListClient.getACLSystemsOwnedByRefs(
            backstageIdentity.ownershipEntityRefs
              .map(ref =>
                // WIT-5372 - this is needed to cover both compact and expanded refs in ACLs due to a bug
                ref.includes('default/')
                  ? [ref.replace('default/', ''), ref]
                  : [ref],
              )
              .flat(),
            environment.name,
          )) ?? []
        );
      } catch (error) {
        alertApi.post({
          error,
          severity: 'error',
        });
      }
    }
    return undefined;
  }, [environment, backstageIdentity]);

  useEffect(() => {
    setSystems(
      value?.map(v => ({
        displayName: v.systemDisplayName,
        id: v.systemId,
        kind: 'Data Product',
        domain: v.domain,
        systemVersion: v.systemVersion,
      })) ?? [],
    );
  }, [value]);

  useEffect(() => {
    const aclSystemOwned = value?.find(dp => dp.systemId === selectedRow?.id);
    setSystemSelected(aclSystemOwned);
  }, [selectedRow, value]);

  return (
    <WbCard
      fillContainer
      title="Your Data Access List"
      actions={<WbEnvironmentSelector />}
    >
      <div className={classes.dockableContainer}>
        <WbDockableGroup direction="horizontal">
          <WbDockablePanel
            defaultSize={30}
            title="Systems"
            showCollapsedContent
          >
            <SystemList
              selectedRow={selectedRow}
              setSelectedRow={setSelectedRow}
              systems={systems}
            />
          </WbDockablePanel>
          <WbDockableResizeHandle direction="horizontal" />
          <WbDockablePanel
            showCollapsedContent
            title={`${systemSelected?.systemDisplayName ?? ''} Consumables`}
            minSize={30}
          >
            <AccessControlList selectedRow={systemSelected} />
          </WbDockablePanel>
        </WbDockableGroup>
      </div>
    </WbCard>
  );
};
